import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { FileText, Sparkles, Target, Download, ArrowRight, CheckCircle } from 'lucide-react';

const features = [
  {
    icon: Target,
    title: 'ATS Optimization',
    description: 'Our AI ensures your resume passes Applicant Tracking Systems with flying colors.',
  },
  {
    icon: Sparkles,
    title: 'Keyword Enhancement',
    description: 'Automatically add relevant keywords from the job description to your resume.',
  },
  {
    icon: Download,
    title: 'Instant Download',
    description: 'Download your optimized resume as a professional PDF ready to submit.',
  },
];

const steps = [
  { step: 1, title: 'Paste Job Description', description: 'Copy the job posting you want to apply for' },
  { step: 2, title: 'Upload Your Resume', description: 'Upload your existing resume in PDF format' },
  { step: 3, title: 'Get Optimized Resume', description: 'AI enhances your resume for the specific role' },
];

const Dashboard = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="h-4 w-4" />
              AI-Powered Resume Optimization
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
              Tailor Your Resume to{' '}
              <span className="text-accent">Any Job Posting</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed">
              Paste a job description, upload your resume, and let our AI optimize it for ATS systems. Stand out from the competition in minutes.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to={isAuthenticated ? "/optimizer" : "/signup"}>
                <Button 
                  className="h-12 px-8 bg-accent hover:bg-accent/90 text-white font-semibold text-base"
                  data-testid="hero-cta"
                >
                  {isAuthenticated ? 'Optimize Resume' : 'Get Started Free'}
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </Link>
              {!isAuthenticated && (
                <Link to="/login">
                  <Button 
                    variant="outline" 
                    className="h-12 px-8 font-medium text-base"
                    data-testid="hero-login"
                  >
                    Sign In
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
      
      {/* How It Works */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Three simple steps to get your ATS-optimized resume
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((item, index) => (
              <div key={index} className="relative">
                <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow h-full">
                  <CardHeader>
                    <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mb-4">
                      <span className="text-accent font-bold text-xl">{item.step}</span>
                    </div>
                    <CardTitle className="text-xl font-semibold text-slate-900">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-slate-600 text-base">
                      {item.description}
                    </CardDescription>
                  </CardContent>
                </Card>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="h-6 w-6 text-slate-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Features */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Why Choose Canva For Resume?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our AI-powered tool helps you create the perfect resume for every application
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow bg-white">
                <CardHeader>
                  <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-accent" />
                  </div>
                  <CardTitle className="text-xl font-semibold text-slate-900">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-slate-600 text-base">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-slate-900 border-0 overflow-hidden">
            <CardContent className="p-8 md:p-12 text-center">
              <div className="max-w-2xl mx-auto">
                <div className="w-16 h-16 bg-accent/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <FileText className="h-8 w-8 text-accent" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Ready to Land Your Dream Job?
                </h2>
                <p className="text-lg text-slate-400 mb-8">
                  Start optimizing your resume today and increase your chances of getting noticed by recruiters.
                </p>
                <Link to={isAuthenticated ? "/optimizer" : "/signup"}>
                  <Button 
                    className="h-12 px-8 bg-accent hover:bg-accent/90 text-white font-semibold text-base"
                    data-testid="cta-button"
                  >
                    {isAuthenticated ? 'Optimize Your Resume' : 'Get Started Free'}
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300\" data-testid="dashboard-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           {/* Top Row */}
           <div className="py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 border-b border-slate-700/50">
            {/* Brand */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 bg-accent rounded-lg flex items-center justify-center">
                  <FileText className="h-4.5 w-4.5 text-white" />
                </div>
                <span className="text-lg font-bold text-white tracking-tight">Canva For Resume</span>
              </div>
              <p className="text-sm text-slate-400 max-w-xs leading-relaxed">
                AI-powered resume optimization that helps you land interviews faster.
              </p>
            </div>
            {/* Policy Links */}
            <nav className="flex flex-wrap items-center gap-x-8 gap-y-3\" data-testid="footer-policy-links\">
              <a
                href="/terms\"
                target="_blank\"
                rel="noopener noreferrer\"
                className="text-sm text-slate-400 hover:text-white transition-colors duration-200\"
                data-testid="footer-terms-link\"
              >
                Terms of Service
              </a>
              <a
                href="/privacy\"
                target="_blank\"
                rel="noopener noreferrer\"
                className="text-sm text-slate-400 hover:text-white transition-colors duration-200\"
                data-testid="footer-privacy-link\"
              >
                Privacy Policy
              </a>
              <a
                href="/refund\"
                target="_blank\"
                rel="noopener noreferrer\"
                className="text-sm text-slate-400 hover:text-white transition-colors duration-200\"
                data-testid="footer-refund-link\"
              >
                Refund Policy
              </a>
            </nav>
          </div>

          {/* Bottom Row */}
          <div className="py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-slate-500\" data-testid="footer-copyright">
              &copy;2026 Canva For Resume, a product of Triplenine Labs.
            </p>
            <p className="text-xs text-slate-600">
              Built with care for job seekers everywhere.
            </p>
            <p className="text-sm text-slate-500">
              ©2026 Canva For Resume, a product of Triplenine Labs.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
